// Editorial dates stay fixed across builds and change only when the prices
// and owner sources are re-checked. The Contentful node keeps the route,
// original publish date and guide-hub listing; the copy lives here.
const printerCostGuide = {
  path: '/guides/how-much-does-a-3d-printer-cost/',
  title: 'How much does a 3D printer cost in 2026?',
  metaTitle: 'How Much Does a 3D Printer Cost? 2026 Prices & Running Costs',
  description:
    '2026 3D printer prices from official stores, owner-measured electricity and running costs, the extras owners buy, and when a print service costs less.',
  reviewedDate: '2026-10-27',
  reviewedDateLabel: 'October 27, 2026',
  pricesCheckedLabel: 'October 27, 2026',
}

// US residential average, EIA Electric Power Monthly Table 5.6.A.
const electricity = {
  centsPerKwh: 18.16,
  month: 'August 2026',
  source:
    'https://www.eia.gov/electricity/monthly/epm_table_grapher.php?t=epmt_5_6_a',
}

// Prices are what each official US store showed on the check date: the
// current price first, then the regular price when a sale was running.
// affiliateUrl stays null until a program is approved; the guide then links
// to it with rel="sponsored" and shows the disclosure automatically.
const printers = [
  {
    tier: 'entry',
    name: 'Creality Ender-3 V3 SE',
    type: 'Filament (FDM)',
    price: '$179',
    regular: '$199',
    storeUrl: 'https://store.creality.com/collections/3d-printers',
    affiliateUrl: null,
  },
  {
    tier: 'entry',
    name: 'Bambu Lab A1 mini',
    type: 'Filament (FDM)',
    price: '$199',
    regular: '$219',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    tier: 'entry',
    name: 'Elegoo Neptune 4 Pro',
    type: 'Filament (FDM)',
    price: '$239',
    regular: '$274',
    storeUrl: 'https://us.elegoo.com/collections/3d-printers',
    affiliateUrl: null,
  },
  {
    tier: 'mid',
    name: 'Bambu Lab A1',
    type: 'Filament (FDM)',
    price: '$259',
    regular: '$299',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    tier: 'mid',
    name: 'Elegoo Centauri Carbon 2',
    type: 'Filament, enclosed',
    price: '$319',
    regular: '$359',
    storeUrl: 'https://us.elegoo.com/collections/3d-printers',
    affiliateUrl: null,
  },
  {
    tier: 'mid',
    name: 'Bambu Lab P1S',
    type: 'Filament, enclosed',
    price: '$349',
    regular: '$399',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    tier: 'mid',
    name: 'Bambu Lab P2S',
    type: 'Filament, enclosed',
    price: '$449',
    regular: '$549',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    tier: 'mid',
    name: 'Creality K1C (2025)',
    type: 'Filament, enclosed',
    price: '$499',
    regular: '$559',
    storeUrl: 'https://store.creality.com/collections/3d-printers',
    affiliateUrl: null,
  },
  {
    tier: 'mid',
    name: 'Prusa MK4S',
    type: 'Filament (FDM)',
    price: '$657 kit',
    regular: '$925 assembled',
    storeUrl: 'https://www.prusa3d.com/category/original-prusa-mk4s/',
    affiliateUrl: null,
  },
  {
    tier: 'prosumer',
    name: 'Prusa CORE One+',
    type: 'Filament, enclosed',
    price: '$925 kit',
    regular: '$1,203 assembled',
    storeUrl: 'https://www.prusa3d.com/category/prusa-core-one/',
    affiliateUrl: null,
  },
  {
    tier: 'prosumer',
    name: 'Bambu Lab H2D',
    type: 'Filament, dual nozzle',
    price: '$1,399',
    regular: '$1,549',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    tier: 'resin',
    name: 'Elegoo Mars 5',
    type: 'Resin (MSLA)',
    price: '$179',
    regular: '$225',
    storeUrl: 'https://us.elegoo.com/collections/3d-printers',
    affiliateUrl: null,
  },
  {
    tier: 'resin',
    name: 'Elegoo Mars 5 Ultra',
    type: 'Resin (MSLA)',
    price: '$259',
    regular: '$338',
    storeUrl: 'https://us.elegoo.com/collections/3d-printers',
    affiliateUrl: null,
  },
  {
    tier: 'resin',
    name: 'Elegoo Saturn 4 Ultra 16K',
    type: 'Resin (MSLA)',
    price: '$399',
    regular: '$649.99',
    storeUrl: 'https://us.elegoo.com/collections/3d-printers',
    affiliateUrl: null,
  },
  {
    tier: 'professional',
    name: 'Formlabs Form 4',
    type: 'Resin (MSLA), Basic Package',
    price: '$2,625',
    regular: null,
    storeUrl: 'https://formlabs.com/store/printers/form-4/',
    affiliateUrl: null,
  },
  {
    tier: 'professional',
    name: 'Formlabs Fuse 1+ 30W',
    type: 'Powder (SLS), printer only',
    price: '$24,649',
    regular: null,
    storeUrl: 'https://formlabs.com/store/printers/fuse-1/',
    affiliateUrl: null,
  },
]

const tiers = [
  {
    id: 'entry',
    label: 'Entry filament printers',
    range: '$179–$274',
    note: 'Open-frame printers for PLA and PETG. Auto bed leveling is now standard at this price.',
  },
  {
    id: 'mid',
    label: 'Mid-range filament printers',
    range: '$259–$925',
    note: 'Faster printers, larger beds and, from about $319, enclosures for ABS, ASA and nylon.',
  },
  {
    id: 'prosumer',
    label: 'Prosumer and multi-material',
    range: '$925–$1,549',
    note: 'Larger enclosed printers, multi-tool or dual-nozzle heads, heavier daily use.',
  },
  {
    id: 'resin',
    label: 'Consumer resin printers',
    range: '$179–$650',
    note: 'Fine detail for miniatures and jewelry masters. Wash, cure and safety gear are extra.',
  },
  {
    id: 'professional',
    label: 'Professional and industrial',
    range: '$2,625–$24,649+',
    note: 'Formlabs publishes these prices; most industrial machines are quote-only.',
  },
]

const consumables = [
  {
    item: 'PLA filament, 1 kg',
    price: '$9.74–$23.99',
    detail:
      'Bambu Lab PLA Basic on sale ($14.99 MSRP) up to Prusament PLA, on each brand’s store.',
    sources: [
      'https://us.store.bambulab.com/products/pla-basic-filament',
      'https://www.prusa3d.com/category/prusament-pla/',
    ],
  },
  {
    item: 'Standard resin',
    price: 'from $18.99',
    detail: 'Elegoo Standard Resin, smallest bottle on its US store.',
    sources: ['https://us.elegoo.com/collections/resin'],
  },
]

// Owner videos are summarized, never transcribed. Timestamps point to the
// moment each figure is stated.
const ownerSources = {
  kaizen: {
    title: 'Measuring how much electricity my bambulabs 3d printers use',
    channel: 'Kaizen 3D Prints',
    date: 'February 2024',
    url: 'https://www.youtube.com/watch?v=vSL5py3iIow',
  },
  tkc: {
    title: 'How Much Power Does the Bambu Lab A1 Use?',
    channel: 'TKC3D',
    date: 'September 2024',
    url: 'https://www.youtube.com/watch?v=GC-N_fTej0E',
  },
  carlex: {
    title: 'Testing the Creality K2 Plus Electrical Power Usage in Watts',
    channel: 'Carlex DIY',
    date: 'December 2024',
    url: 'https://www.youtube.com/watch?v=ep8C0OfCOq0',
  },
  loyal: {
    title: 'Revealing the TRUE Costs of 3D Printing!',
    channel: 'Loyal Moses',
    date: 'August 2024',
    url: 'https://www.youtube.com/watch?v=NB_4N7lTgwE',
  },
  relentless: {
    title: 'Is 3D Resin Printing actually affordable?',
    channel: 'RelentlesStorm',
    date: 'June 2026',
    url: 'https://www.youtube.com/watch?v=3eiec0rgdaQ',
  },
  sword: {
    title: 'The TRUE COST of 3D Printing',
    channel: 'Sword ’n’ Steele',
    date: 'October 2026',
    url: 'https://www.youtube.com/watch?v=wBz4BzoUB2M',
  },
}

const at = (key, seconds) => `${ownerSources[key].url}&t=${seconds}s`

const centsFor = (watts, hours = 1) =>
  (((watts * hours) / 1000) * electricity.centsPerKwh).toFixed(1)

const powerReadings = [
  {
    printer: 'Bambu Lab A1 Combo',
    source: 'tkc',
    idle: '6.7 W',
    printing: '90–130 W',
    spike: '1,148 W for about 20 seconds at print start',
    perHour: centsFor(110),
    idleLink: at('tkc', 90),
    printingLink: at('tkc', 122),
  },
  {
    printer: 'Bambu Lab printer (one of seven)',
    source: 'kaizen',
    idle: '4.7 W',
    printing: '103.6 W',
    spike: null,
    perHour: centsFor(103.6),
    idleLink: at('kaizen', 6),
    printingLink: at('kaizen', 25),
  },
  {
    printer: 'Creality K2 Plus',
    source: 'carlex',
    idle: '22–24 W (it does not sleep)',
    printing: '180–200 W',
    spike: 'about 920 W at print start',
    perHour: centsFor(190),
    idleLink: at('carlex', 124),
    printingLink: at('carlex', 493),
  },
]

const ownerExtras = [
  {
    item: 'Filament dryer',
    cost: '$50–$100',
    why: 'Filament absorbs moisture, which causes brittle prints and clogs.',
    link: at('loyal', 597),
    source: 'loyal',
  },
  {
    item: 'Airtight storage',
    cost: '$10–$20 per container',
    why: 'Each container holds only a few spools.',
    link: at('loyal', 574),
    source: 'loyal',
  },
  {
    item: 'Enclosure',
    cost: '$50–$300+',
    why: 'Contains fumes from ABS, ASA and nylon and steadies temperature.',
    link: at('loyal', 664),
    source: 'loyal',
  },
  {
    item: 'Replacement nozzles and PTFE tubing',
    cost: 'a few dollars to $20+',
    why: 'Abrasive filaments such as carbon-fiber blends wear nozzles faster.',
    link: at('loyal', 92),
    source: 'loyal',
  },
  {
    item: 'Hotend and extruder upgrades',
    cost: '$50–$100+ and $100+',
    why: 'For higher-temperature or flexible materials on older printers.',
    link: at('loyal', 435),
    source: 'loyal',
  },
  {
    item: 'Hex drivers and digital calipers',
    cost: '$20–$30 and $20–$50',
    why: 'Stock tools are fine at first, then owners replace them.',
    link: at('loyal', 502),
    source: 'loyal',
  },
  {
    item: 'Respirator and filters',
    cost: '$30–$90, plus filters',
    why: 'For resin and fume-heavy materials.',
    link: at('loyal', 690),
    source: 'loyal',
  },
  {
    item: 'Fire extinguisher',
    cost: '$20–$50',
    why: 'Printers run unattended for hours.',
    link: at('loyal', 717),
    source: 'loyal',
  },
]

const faqs = [
  {
    question: 'How much is a good 3D printer for a beginner?',
    answer:
      'On official US stores in late October 2026, capable beginner filament printers cost $179–$299, for example the Creality Ender-3 V3 SE, Bambu Lab A1 mini and Bambu Lab A1. Owners commonly add $50–$300 or more in extras, such as a filament dryer, storage and an enclosure.',
  },
  {
    question: 'How much does it cost to run a 3D printer per hour?',
    answer: `Owner measurements put typical home filament printers at roughly 100–200 W while printing. At the ${electricity.month} US residential average of ${electricity.centsPerKwh}¢ per kWh, that is about ${centsFor(100)}–${centsFor(200)}¢ of electricity per print-hour. Filament usually costs far more than the power: a 1 kg spool of PLA costs $9.74–$23.99.`,
  },
  {
    question: 'Do 3D printers use a lot of electricity?',
    answer:
      'Not while printing. A Bambu Lab A1 owner measured 90–130 W during a print, and a 3.5-hour print used 0.5 kWh. Larger enclosed printers draw more: a Creality K2 Plus owner measured 180–200 W, with a brief spike of about 920 W at the start of each print, which matters when several printers share one circuit.',
  },
  {
    question: 'Is a resin printer cheaper than a filament printer?',
    answer:
      'The printers cost about the same, roughly $179–$400 for consumer models. Resin costs more to run. Owners add isopropyl alcohol, nitrile gloves, a respirator, a wash-and-cure station and a ventilated space. One owner puts the full resin start-up at $400 to over $2,000.',
  },
  {
    question: 'Is it cheaper to buy a 3D printer or use a 3D printing service?',
    answer:
      'Buying is usually cheaper if you will print small parts often and enjoy tuning a machine. A service usually makes more sense for one-off pieces, large parts, engineering or specialty materials, painted or finished work, or a fixed deadline, where a failed print or a new machine would cost more than the order.',
  },
]

module.exports = {
  printerCostGuide,
  electricity,
  printers,
  tiers,
  consumables,
  ownerSources,
  powerReadings,
  ownerExtras,
  faqs,
  centsFor,
}
